import React from 'react';

const SIZE_OPTIONS = {
  men: ['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL', '4XL'],
  women: ['XS', 'S', 'M', 'L', 'XL', 'XXL'],
  kids: ['2-3Y', '4-5Y', '6-7Y', '8-9Y', '10-11Y', '12-13Y'],
  sports: ['S', 'M', 'L', 'XL', 'XXL'],
};

const COLOR_OPTIONS = [
  'Black',
  'White',
  'Red',
  'Blue',
  'Green',
  'Yellow',
  'Orange',
  'Pink',
  'Purple',
  'Brown',
  'Grey',
  'Navy',
];

const resolveSizeOptions = (category) => {
  const c = (category || '').toLowerCase();
  if (c.includes('women') || c.includes('ladies') || c.includes('female')) return SIZE_OPTIONS.women;
  if (c.includes('kids') || c.includes('kid') || c.includes('boys') || c.includes('girls')) return SIZE_OPTIONS.kids;
  if (c.includes('sports')) return SIZE_OPTIONS.sports;
  if (c.includes('men') || c.includes('male')) return SIZE_OPTIONS.men;
  return [];
};

const normalizeVariant = (variant) => {
  const sizes = Array.isArray(variant.sizes) ? variant.sizes : [];
  const colors = Array.isArray(variant.colors) ? variant.colors : [];
  const packTotal = Number(variant.pack_count || variant.quantity || 0);
  if (sizes.length && packTotal > 0) {
    const per = Math.floor(packTotal / sizes.length);
    const remainder = packTotal % sizes.length;
    const sizeQuantities = {};
    sizes.forEach((s, idx) => {
      sizeQuantities[s] = per + (idx < remainder ? 1 : 0);
    });
    return {
      ...variant,
      colors,
      pack_count: packTotal,
      quantity: packTotal,
      size_quantities: sizeQuantities,
    };
  }
  return {
    ...variant,
    colors,
    pack_count: packTotal || variant.pack_count,
    quantity: packTotal || variant.quantity,
  };
};

const isFile = (value) => typeof File !== 'undefined' && value instanceof File;

const ProductCreatePage = ({
  productForm,
  setProductForm,
  addProduct,
  productMessage,
  categories = [],
  subCategories = [],
}) => {
  const variants = React.useMemo(
    () => (Array.isArray(productForm.variants) ? productForm.variants : []),
    [productForm.variants]
  );
  const images = React.useMemo(
    () => (Array.isArray(productForm.images) ? productForm.images : []),
    [productForm.images]
  );
  const filteredSubCategories = (subCategories || []).filter(
    (s) =>
      String(s?.category_name || '').trim().toLowerCase() ===
      String(productForm.category || '').trim().toLowerCase()
  );

  const updateVariant = (index, key, value) => {
    const next = variants.map((v, i) =>
      i === index ? normalizeVariant({ ...v, [key]: value }) : v
    );
    setProductForm({ ...productForm, variants: next });
  };

  const handleMainPriceChange = (value) => {
    const nextVariants = variants.map((v) => normalizeVariant({ ...v, price_per_piece: value }));
    setProductForm({ ...productForm, main_price: value, variants: nextVariants });
  };

  const addVariant = () => {
    setProductForm({
      ...productForm,
      variants: [
        ...variants,
        {
          sizes: [],
          colors: [],
          color_input: '',
          color_images: {},
          pack_count: 0,
          price_per_piece: productForm.main_price || 0,
          quantity: 0,
        },
      ],
    });
  };

  const removeVariant = (index) => {
    setProductForm({
      ...productForm,
      variants: variants.filter((_, i) => i !== index),
    });
  };

  const toggleSize = (index, size) => {
    const current = Array.isArray(variants[index]?.sizes) ? variants[index].sizes : [];
    const nextSizes = current.includes(size)
      ? current.filter((s) => s !== size)
      : [...current, size];
    const next = variants.map((v, i) =>
      i === index ? normalizeVariant({ ...v, sizes: nextSizes }) : v
    );
    setProductForm({ ...productForm, variants: next });
  };

  const toggleColor = (index, color) => {
    const current = Array.isArray(variants[index]?.colors) ? variants[index].colors : [];
    const nextColors = current.includes(color)
      ? current.filter((c) => c !== color)
      : [...current, color];
    const currentImages = variants[index]?.color_images || {};
    const nextImages = {};
    nextColors.forEach((c) => {
      if (currentImages[c]) nextImages[c] = currentImages[c];
    });
    const next = variants.map((v, i) =>
      i === index ? normalizeVariant({ ...v, colors: nextColors, color_images: nextImages }) : v
    );
    setProductForm({ ...productForm, variants: next });
  };

  const addCustomColor = (index) => {
    const value = (variants[index]?.color_input || '').trim();
    if (!value) return;
    const current = Array.isArray(variants[index]?.colors) ? variants[index].colors : [];
    if (current.includes(value)) {
      const next = variants.map((v, i) =>
        i === index ? { ...v, color_input: '' } : v
      );
      setProductForm({ ...productForm, variants: next });
      return;
    }
    const nextColors = [...current, value];
    const next = variants.map((v, i) =>
      i === index ? normalizeVariant({ ...v, colors: nextColors, color_input: '' }) : v
    );
    setProductForm({ ...productForm, variants: next });
  };

  const setColorImage = (index, color, file) => {
    const next = variants.map((v, i) => {
      if (i !== index) return v;
      const currentImages = v.color_images || {};
      return {
        ...v,
        color_images: {
          ...currentImages,
          [color]: file || null,
        },
      };
    });
    setProductForm({ ...productForm, variants: next });
  };

  const handleGalleryChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setProductForm({
      ...productForm,
      images: [...images, ...files],
    });
    e.target.value = '';
  };

  const removeGalleryImage = (idx) => {
    const next = images.filter((_, i) => i !== idx);
    setProductForm({ ...productForm, images: next });
  };

  const mainImagePreview = React.useMemo(
    () => (isFile(productForm.main_image) ? URL.createObjectURL(productForm.main_image) : ''),
    [productForm.main_image]
  );

  React.useEffect(
    () => () => {
      if (mainImagePreview) URL.revokeObjectURL(mainImagePreview);
    },
    [mainImagePreview]
  );

  const galleryPreviews = React.useMemo(
    () =>
      images.map((file, idx) => ({
        idx,
        name: file?.name || `Image ${idx + 1}`,
        url: isFile(file) ? URL.createObjectURL(file) : '',
      })),
    [images]
  );

  React.useEffect(
    () => () => {
      galleryPreviews.forEach((item) => {
        if (item.url) URL.revokeObjectURL(item.url);
      });
    },
    [galleryPreviews]
  );

  const variantColorPreviews = React.useMemo(() => {
    const previews = {};
    variants.forEach((variant, variantIndex) => {
      const colorImages = variant?.color_images || {};
      Object.entries(colorImages).forEach(([color, file]) => {
        if (!isFile(file)) return;
        previews[`${variantIndex}__${String(color)}`] = URL.createObjectURL(file);
      });
    });
    return previews;
  }, [variants]);

  React.useEffect(
    () => () => {
      Object.values(variantColorPreviews).forEach((url) => URL.revokeObjectURL(url));
    },
    [variantColorPreviews]
  );

  return (
    <div className="card product-create-page">
      <div className="product-create-head">
        <div>
          <h3>Create Product</h3>
          <p className="muted">Configure core info, variants, and media for your B2B catalog listing.</p>
        </div>
        <div className="row product-create-meta">
          <span className="status-badge">Variants: {variants.length}</span>
          <span className="badge-soft">Gallery: {images.length}</span>
        </div>
      </div>

      <div className="product-create-section">
        <h4>Core Details</h4>
        <div className="grid">
          <div>
            <label>Name</label>
            <input value={productForm.name} onChange={(e) => setProductForm({ ...productForm, name: e.target.value })} />
          </div>
          <div>
            <label>Brand</label>
            <input value={productForm.brand} onChange={(e) => setProductForm({ ...productForm, brand: e.target.value })} />
          </div>
          <div>
            <label>Category</label>
            <select
              value={productForm.category}
              onChange={(e) => setProductForm({ ...productForm, category: e.target.value, sub_category: '' })}
            >
              <option value="">Select Category</option>
              {(categories || []).map((c) => (
                <option key={c._id} value={c.name}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label>Sub Category</label>
            <select
              value={productForm.sub_category}
              onChange={(e) => setProductForm({ ...productForm, sub_category: e.target.value })}
              disabled={!productForm.category}
            >
              <option value="">Select Sub Category</option>
              {filteredSubCategories.map((s) => (
                <option key={s._id} value={s.name}>{s.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label>Price Per Piece</label>
            <input
              type="number"
              value={productForm.main_price}
              onChange={(e) => handleMainPriceChange(e.target.value)}
            />
          </div>
          <div>
            <label>Discount %</label>
            <input
              type="number"
              value={productForm.discount_percentage}
              onChange={(e) => setProductForm({ ...productForm, discount_percentage: e.target.value })}
            />
          </div>
          <div>
            <label>Quantity</label>
            <input
              type="number"
              value={productForm.quantity}
              onChange={(e) => setProductForm({ ...productForm, quantity: e.target.value })}
            />
          </div>
          
        </div>
      </div>

      <div className="product-create-section product-variant-theme">
        <div className="row product-create-section-head">
          <h4>Variants (size, color, pack)</h4>
          <button className="secondary" type="button" onClick={addVariant}>Add Variant</button>
        </div>
        {variants.length === 0 ? (
          <div className="product-empty-state">No variants yet. Add your first variant to define available color-size packs.</div>
        ) : (
          <div className="list">
            {variants.map((variant, index) => (
              <div key={`variant-${index}`} className="list-item product-variant-card">
                <div className="row product-variant-head">
                  <div>
                    <strong>Variant {index + 1}</strong>
                    <div className="muted">
                      {(variant.colors || []).length} colors | {(variant.sizes || []).length} sizes
                    </div>
                  </div>
                  <button className="secondary" type="button" onClick={() => removeVariant(index)}>Remove</button>
                </div>

                <div className="grid">
                  <div>
                    <label>Pack Count (Total Qty)</label>
                    <input
                      type="number"
                      value={variant.pack_count}
                      onChange={(e) => updateVariant(index, 'pack_count', e.target.value)}
                    />
                  </div>
                  <div>
                    <label>Price / Piece</label>
                    <input
                      type="number"
                      value={productForm.main_price || variant.price_per_piece || ''}
                      readOnly
                    />
                  </div>
                  <div>
                    <label>Auto Size Split</label>
                    <div className="muted">
                      {variant.size_quantities
                        ? Object.entries(variant.size_quantities).map(([size, qty]) => `${size}: ${qty}`).join(', ')
                        : 'Select sizes and pack count to auto split'}
                    </div>
                  </div>
                </div>

                <div className="product-chip-block">
                  <label>Colors</label>
                  <div className="product-chip-row">
                    {COLOR_OPTIONS.map((color) => {
                      const selected = (variant.colors || []).includes(color);
                      return (
                        <button
                          key={`${index}-${color}`}
                          type="button"
                          className={`chip-toggle ${selected ? 'active' : ''}`}
                          onClick={() => toggleColor(index, color)}
                        >
                          {color}
                        </button>
                      );
                    })}
                  </div>
                  <div className="row product-inline-form">
                    <input
                      placeholder="Add custom color"
                      value={variant.color_input || ''}
                      onChange={(e) => updateVariant(index, 'color_input', e.target.value)}
                    />
                    <button className="secondary" type="button" onClick={() => addCustomColor(index)}>Add</button>
                  </div>
                </div>

                <div className="product-chip-block">
                  <label>Sizes</label>
                  <div className="product-chip-row">
                    {resolveSizeOptions(productForm.category).map((size) => {
                      const selected = (variant.sizes || []).includes(size);
                      return (
                        <button
                          key={`${index}-${size}`}
                          type="button"
                          className={`chip-toggle ${selected ? 'active' : ''}`}
                          onClick={() => toggleSize(index, size)}
                        >
                          {size}
                        </button>
                      );
                    })}
                  </div>
                  {resolveSizeOptions(productForm.category).length === 0 ? (
                    <div className="muted">Select category such as Men/Women/Kids/Sports to enable size presets.</div>
                  ) : null}
                </div>

                <div>
                  <label>Color-wise Images</label>
                  {(variant.colors || []).length ? (
                    <div className="list">
                      {(variant.colors || []).map((color) => (
                        <div key={`${index}-${color}`} className="row product-color-image-row">
                          <span className="badge-pill">{color}</span>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => setColorImage(index, color, e.target.files?.[0] || null)}
                          />
                          {variant.color_images?.[color]?.name ? (
                            <span className="muted">{variant.color_images[color].name}</span>
                          ) : (
                            <span className="muted">No image selected</span>
                          )}
                          {variantColorPreviews[`${index}__${String(color)}`] ? (
                            <div className="product-color-image-preview">
                              <img
                                src={variantColorPreviews[`${index}__${String(color)}`]}
                                alt={`${color} preview`}
                              />
                            </div>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="muted">Select colors first to attach per-color images.</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="product-create-section">
        <h4>Media</h4>
        <div className="grid">
          <div>
            <label>Main Image</label>
            <input type="file" onChange={(e) => setProductForm({ ...productForm, main_image: e.target.files[0] })} />
            {mainImagePreview ? (
              <div className="product-main-image-preview">
                <img src={mainImagePreview} alt="Main preview" />
              </div>
            ) : null}
          </div>
          <div>
            <label>Gallery Images</label>
            <input type="file" multiple accept="image/*" onChange={handleGalleryChange} />
            {galleryPreviews.length ? (
              <div className="product-gallery-grid">
                {galleryPreviews.map((item) => (
                  <div key={`${item.name}-${item.idx}`} className="product-gallery-item">
                    <div className="product-gallery-thumb">
                      {item.url ? <img src={item.url} alt={item.name} /> : <span>No preview</span>}
                    </div>
                    <div className="product-gallery-name">{item.name}</div>
                    <button
                      type="button"
                      className="secondary product-gallery-remove"
                      onClick={() => removeGalleryImage(item.idx)}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="muted" style={{ marginTop: 6 }}>
                Select multiple images (use Ctrl/Shift to multi-select).
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="product-create-section">
        <label>Description</label>
        <textarea
          value={productForm.description}
          onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
        />
      </div>

      <div className="row">
        <button onClick={addProduct}>Add Product</button>
        {productMessage ? <div className="muted">{productMessage}</div> : null}
      </div>
    </div>
  );
};

export default ProductCreatePage;
